import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../util/classNameConstructor";
import Button from "../../../core/button/Button";
import Checkbox from "../../../core/checkbox/Checkbox";
import Icon from "../../../core/icon/Icon";
import TooltipProvider from "../../../core/tooltip/TooltipProvider";
import { useToPoints } from "../questions/ParticipatoryBudget";
import type { Question } from "../survey.types";
import { isChecked, toggle } from "../utils";
import { useGlobalState } from "senf-shared/index";

export const useChoiceState = (
  question: Question,
  rawValue: any,
  option: NonNullable<Question["options"]>[number],
  column?: NonNullable<Question["columns"]>[number],
) => {
  const { t } = useTranslation();
  const { qtype, maxChoices, multipleChoice } = question || {};
  const columnId = column?.uid || "column";
  const type =
    question.qtype !== "budget" &&
    (multipleChoice !== true ||
      question.qtype === "ratingScale" ||
      question.qtype === "stars")
      ? "radio"
      : "checkbox";
  const value = useMemo(
    () =>
      question.qtype === "radioMatrix" ||
      question.qtype === "ratingScale" ||
      question.qtype === "stars"
        ? rawValue?.[option.uid]
        : rawValue,
    [rawValue, option.uid, question.qtype],
  );

  const checked = useMemo(
    () =>
      isChecked(
        value,
        question.qtype === "radioMatrix" ||
          question.qtype === "ratingScale" ||
          question.qtype === "stars"
          ? columnId
          : option.uid,
        type,
      ),
    [value, columnId, option.uid, type],
  );

  const spending =
    question.options?.reduce(
      (acc, curr) =>
        isChecked(value, curr.uid, type) ? acc + (curr.price || 0) : acc,
      0,
    ) || 0;

  const disabled = useMemo(() => {
    switch (qtype) {
      case "radioMatrix":
        if (
          maxChoices &&
          multipleChoice &&
          value?.length >= maxChoices &&
          !checked
        ) {
          return t("survey:disabledMessages.radioMatrix.maxChoices", {
            count: maxChoices || 0,
          });
        }
        break;
      case "radio":
      case "imageChoice":
        if (
          maxChoices &&
          multipleChoice &&
          value?.length >= maxChoices &&
          !checked
        ) {
          return t("survey:disabledMessages.radio.maxChoices", {
            count: maxChoices || 0,
          });
        }
        break;

      case "budget":
        if (
          (question.budget || 0) - spending < (option.price || 0) &&
          !checked
        ) {
          return t("global:noMoreRemainingBudget");
        }
        break;
      default:
        return;
    }
  }, [qtype, maxChoices, multipleChoice, value, checked, t]);

  return { disabled, checked, type };
};

type ChoiceItemWrapperProps = {
  question: Question;
  option: NonNullable<Question["options"]>[number];
  column?: NonNullable<Question["columns"]>[number];
  value: any;
  comment?: string;
};
export const ChoiceItemWrapper = ({
  question,
  column,
  option,
  value,
  children,
}: ChoiceItemWrapperProps & { children: React.ReactNode }) => {
  const { disabled } = useChoiceState(question, value, option, column);
  return (
    <TooltipProvider
      critical
      disabled={!!disabled}
      primaryContent={disabled}
      noTooltip={!disabled}
      className={cn(
        "flex",
        question.qtype === "radio" && disabled && "self-start",
      )}
    >
      {children}
    </TooltipProvider>
  );
};

type ChoiceItemProps = ChoiceItemWrapperProps & {
  onChange: (value: any) => void;
  classNames?: {
    label: string;
  };
};

const ImageElement = ({
  option,
}: { option: NonNullable<Question["options"]>[number] }) => {
  return (
    <div
      className={cn(
        "relative flex items-center justify-center aspect-square w-full border-b border-b-border-subdued group-hover/label:border-b-border-highlight-default group-active/label:border-b-border-highlight-default group-has-[:checked]/label:border-b-border-highlight-default",
      )}
    >
      {option.url ? (
        <>
          <img
            src={option.url}
            className={cn(
              "border-none object-cover aspect-square rounded-lg rounded-b-none rounded-t-xl",
            )}
            alt={option.altText}
            aria-describedby={`figcaption-${option.uid}`}
          />

          <figcaption id={`figcaption-${option.uid}`} className="sr-only">
            {`© ${option.copyright}`}
          </figcaption>
          {option.copyright && (
            <Button
              tooltipLabel={`© ${option.copyright}`}
              leadingIcon="copyright"
              size="sm"
              variant="shapeless"
              leadingIconProps={{
                color: "white",
                className: "drop-shadow-md",
              }}
              className="pointer-events-none"
              classNameWrapper="absolute start-2 bottom-2 z-10 pointer-events-auto"
              tabIndex={-1}
            />
          )}
        </>
      ) : (
        <Icon
          name={option.uid === "other" ? "edit" : "image"}
          className={cn(
            "h-8 w-8 text-text-subdued group-hover/label:text-[var(--primary-800)] group-active/label:text-[var(--primary-800)]",
          )}
        />
      )}
    </div>
  );
};

const TextElement = ({
  question,
  option,
  label,
}: {
  question: Question;
  option: NonNullable<Question["options"]>[number];
  label: string | undefined;
}) => {
  const [translation] = useGlobalState().translationsState;
  const { toPoints } = useToPoints();

  if (question.qtype === "radioMatrix") return null;
  return (
    <div className={cn("w-full")} aria-hidden="true">
      {question.qtype === "budget" ? (
        <span className="flex items-center justify-between w-full gap-5">
          <span>{translation?.[option.value] || option.value}</span>
          <span className="body-md-bold whitespace-nowrap">
            {toPoints(option.price || 0)}
          </span>
        </span>
      ) : label ? (
        <span
          className={cn(question.qtype === "imageChoice" && "inline-block p-3")}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
};

export const ChoiceItem = ({
  question,
  column,
  option,
  value,
  comment,
  onChange,
  classNames,
}: ChoiceItemProps) => {
  const columnId = column?.uid || "column";
  const { disabled, checked, type } = useChoiceState(
    question,
    value,
    option,
    column,
  );
  const { toPoints } = useToPoints();
  const [translation] = useGlobalState().translationsState;

  const handleChange = useCallback(() => {
    if (disabled) return;
    if (question.qtype === "radioMatrix") {
      onChange?.({
        value: {
          ...(value || {}),
          [option.uid]: toggle(value?.[option.uid], columnId, type),
        },
      });
    } else {
      onChange?.({
        value: toggle(value, option.uid, type),
        comment: isChecked(value, "other", type) ? comment : "",
      });
    }
  }, [value, option.uid, columnId, type, onChange, disabled, question.qtype]);

  const inputGroupName = useMemo(() => {
    if (question.qtype === "radioMatrix") {
      return option.uid;
    }
    return columnId;
  }, [option.uid, columnId, question.qtype]);

  const label = useMemo(() => {
    switch (question.qtype) {
      case "radioMatrix":
        return `${translation?.[column?.value] || column?.value}`;
      case "budget":
        return `${translation?.[option.value] || option.value}: ${toPoints(option.price || 0)}`;
      default:
        return translation?.[option.value] || option.value;
    }
  }, [option.value, option.price, toPoints, question.qtype, translation]);

  return (
    <ChoiceItemWrapper
      question={question}
      column={column}
      option={option}
      value={value}
    >
      <label
        htmlFor={`input-${option.uid}-${columnId}`}
        className={cn(
          "group/label relative flex grow items-center justify-center gap-2 p-2 m-1 rounded-lg text-body-md",
          !disabled &&
            `cursor-pointer
            has-[:focus-visible]:bg-surface-highlight-subdued
            hover:bg-action-tertiary-hovered
            hover:has-[:focus-visible]:bg-action-tertiary-hovered
            active:bg-action-tertiary-pressed
            `,
          disabled && "opacity-50 pointer-events-none",
          question.qtype === "radio" && "-mx-2",
          question.qtype === "budget" && "-mx-2",
          question.qtype === "imageChoice" &&
            `
            p-0 rounded-xl gap-y-0
            flex-col justify-start
            bg-surface-subdued
            ring-1 ring-border-subdued
            has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focused-black
            `,
          question.qtype === "imageChoice" &&
            !disabled &&
            "hover:ring-border-highlight-default",
          question.qtype === "imageChoice" &&
            checked &&
            "ring-border-highlight-default bg-surface-highlight-subdued",
          classNames?.label,
        )}
      >
        <input
          type={type}
          id={`input-${option.uid}-${columnId}`}
          name={inputGroupName}
          value={option.value}
          onChange={handleChange}
          checked={checked}
          className="peer sr-only"
          disabled={!!disabled}
          aria-label={label}
        />

        <Checkbox
          checked={checked}
          className="box-content pointer-events-none"
          classNames={{
            wrapper: cn(
              "pointer-events-none",
              question.qtype === "imageChoice" &&
                "absolute top-1.5 end-1.5 z-10",
            ),
            button: cn(`
              group-hover/label:text-border-highlight-default
              group-active/label:text-border-highlight-default
              group-has-[:focus-visible]/label:text-border-highlight-default
              group-has-[:focus-visible]/label:ring-2
              group-has-[:focus-visible]/label:ring-focused-black
            `),
          }}
          aria-hidden
          role="presentation"
          tabIndex={-1}
          type={type as "radio" | "checkbox"}
          disabled={!!disabled}
          noTooltip
        />
        {question.qtype === "imageChoice" && <ImageElement option={option} />}
        <TextElement question={question} option={option} label={label} />
      </label>
    </ChoiceItemWrapper>
  );
};
